// @flow
// Framework
import { ApiServices } from './types';
import type { ApiService } from './types';
import Http, { HTTP_DATA_JSON } from '../network/http';

const ApiError = {
  CRIT: 'crit',
  WARN: 'warn'
};

const warnDescriptions: string[] = [];

function getTimestamp(isMs?: boolean): number {
  if (isMs) {
    return Date.now();
  } else {
    return Math.floor(Date.now() / 1000);
  }
}

function onRequestOk(service: ApiService, payload: Object): ?Object {
  let infos = payload;

  if (infos) {
    if (typeof infos === 'string') {
      infos = JSON.parse(infos);
    }
  }

  return infos;
}

function getCacheInfo(service: ApiService, selector: string): any {
  const infos = cache[service];
  const info = infos && infos[selector];

  // On récupère l'information en cache du service
  return info;
}

function setCacheInfo(service: ApiService, selector: string, info: any) {
  if (!cache[service]) {
    cache[service] = {};
  }

  // On met en cache l'information du service
  cache[service][selector] = info;
}

function addData(service: ApiService, data: ?Object): Object {
  if (!data) {
    data = {};
  }

  // Les infos communes à tous les services
  data.client = client;
  data.application = application;
  data.opid = opid;
  data.device = device;
  data.ver = version;

  if (clientid) {
    data.cid = clientid;
  }

  // Les infos spécifiques aux remontées d'erreurs/warnings
  if (service === ApiServices.ERROR) {
    data.ts = getTimestamp();

    if (nickname) {
      data.login = nickname;
    }
  }
  return data;
}

function checkRateLimit(): boolean {
  if (rateLimitReached) {
    // L'utilisateur a atteint son rate limit, on ne remonte plus d'erreurs pour sa session
    return false;
  }

  const now = getTimestamp(true);

  if (!rateLimitTimestamp) {
    // On initialise le timestamp de détection
    rateLimitTimestamp = now;
  }

  if (now > rateLimitTimestamp + RATE_LIMIT_DURATION) {
    // Le rate limit n'est pas dépassé dans la période, on redémarre un nouveau cycle
    rateLimitTimestamp = now;
    rateLimitCount = 1;
  } else {
    // On compte la nouvelle requête pour la période
    ++rateLimitCount;

    // On regarde si le rate limit a été atteint
    if (rateLimitCount >= RATE_LIMIT_COUNT) {
      Api.request(ApiServices.ERROR, {
        type: ApiError.WARN,
        data: {
          description: 'Rate limit reached',
          timestamp: rateLimitTimestamp,
          duration: RATE_LIMIT_DURATION,
          count: rateLimitCount
        }
      }).catch(() => {});

      // Rate limit atteint
      rateLimitReached = true;
      return false;
    }
  }

  // Rate limit non atteint
  return true;
}

function enqueueError(infos: ?Object) {
  if (errorsQueue && infos) {
    errorsQueue.push(infos);
  }
}

function dequeueError() {
  if (endpoint) {
    const infos = errorsQueue && errorsQueue.shift();

    if (infos) {
      Api.request(ApiServices.ERROR, infos).finally(() => {
        dequeueError();
      });
    }
  }
}

/** Le timeout des requêtes http. */
const TIMEOUT: number = 5000;

/** La durée de détection pour le rate limit, en ms. */
const RATE_LIMIT_DURATION = 60 * 1000;

/** Le nombre maximum de requêtes sur le service d'erreur sur la durée du rate limit. */
const RATE_LIMIT_COUNT = 20;

/** Le cache d'accès aux informations des services. */
const cache: { [key: ApiService]: any } = {};

/** Le endpoint de l'api. */
let endpoint: ?string = null;

/** Le type de client. */
let client: ?string = null;

/** Le type d'application. */
let application: ?string = null;

/** L'identifiant de l'opérateur. */
let opid: ?string = null;

/** L'identifiant du device. */
let device: ?string = null;

/** Le numéro de version de l'application. */
let version: ?string = null;

/** Le nom de l'utilisateur, si connu. */
let nickname: ?string = null;

/** L'identifiant du client, si connu. */
let clientid: ?string = null;

/** Indique si le monitoring des erreurs/warning en temps-réel est activé ou non. */
let monitoring: boolean = true;

/** Liste des services nécessitant un envoie des données en POST */
const PostServices: Array<ApiService> = [ApiServices.ERROR];

/** Le timestamp de début du rate limit. */
let rateLimitTimestamp: number = 0;

/** Le nombre de requêtes en cours pour le rate limit. */
let rateLimitCount: number = 0;

/** Indique si le rate limit a été atteint. */
let rateLimitReached: boolean = false;

/** Erreurs en attente d'envoi. */
let errorsQueue: Array<Object> = [];

/**
 * La classe responsable de communiquer avec les web-services et de fournir les infos associées.
 */
const Api: Object = {};

/**
 * Configure l'api des web-services.
 *
 * @param ep Le endpoint de l'api.
 * @param an Le nom de l'application.
 * @param op L'identifiant de l'opérateur.
 */
Api.configure = function (ep: string, an: string, op: string) {
  endpoint = ep;
  client = 'challenges';
  application = an;
  opid = op;
  version = __BUILD_VERSION__;

  // On envoi les ereurs en attente
  dequeueError();
};

/**
 * Effectue une demande auprès d'un service.
 *
 * @param service Le service demandé.
 * @param infos Les infos à fournir au service.
 * @param timeout La durée du timeout, en millisecondes.
 * @return La promesse associée à la requête ajax.
 */
Api.request = function (service: ApiService, infos?: Object, timeout?: number): Promise<any> {
  if (!endpoint) {
    if (service === ApiServices.ERROR) {
      // L'api n'est pas encore intialisée, on met l'erreur en attente
      enqueueError(infos);
    }

    return Promise.reject();
  }

  // On ajoute les informations nécessaires aux différents services
  infos = addData(service, infos);

  const url = endpoint + '/' + service;
  const isPost = PostServices.indexOf(service) !== -1;

  return new Promise((resolve, reject) => {
    let promise = null;

    if (isPost) {
      promise = Http.post(url, HTTP_DATA_JSON, infos, timeout || TIMEOUT);
    } else {
      promise = Http.get(url, HTTP_DATA_JSON, infos, timeout || TIMEOUT);
    }

    promise
      .then((payload) => {
        // On résoud la requête en utilisant les infos fournies
        const infos = onRequestOk(service, payload);
        resolve(infos);
      })
      .catch(() => {
        // On simule un succès en utilisant les infos sauvegardées
        resolve();
      });
  });
};

/**
 * Active/désactive le monitoring des erreurs/warnings.
 *
 * @param enabled Indique si le monitoring doit être activé ou non.
 */
Api.monitor = function (enabled: boolean) {
  monitoring = !!enabled;
};

/**
 * Reporte un warning en temps réel.
 *
 * @param description La description du warning.
 * @param locals L'état de certaines variables locales.
 * @param isCritical Indique si le warning doit être remonté comme une erreur critique.
 */
Api.warn = function (description: string, locals?: ?Object = null, isCritical?: ?boolean = false) {
  if (monitoring && checkRateLimit()) {
    const data: Object = {
      description
    };

    if (locals) {
      data.locals = locals;
    }

    Api.request(ApiServices.ERROR, {
      type: isCritical ? ApiError.CRIT : ApiError.WARN,
      data: data
    }).catch(() => {});
  }
};

/**
 * Reporte un warning en temps réel si il a pas déjà été reporte (stocké dans warnDescriptions).
 *
 * @param description La description du warning.
 * @param locals L'état de certaines variables locales.
 * @param isCritical Indique si le warning doit être remonté comme une erreur critique.
 */
Api.warnOnce = function (description: string, locals?: ?Object = null, isCritical?: ?boolean = false) {
  if (warnDescriptions.includes(description)) {
    return;
  }
  warnDescriptions.push(description);
  Api.warn(description, locals, isCritical);
};

/**
 * Reporte un erreur critique en temps réel.
 *
 * @param data Les données à reporter (description, callstack, file, line, column).
 */
Api.crit = function (data: Object) {
  if (monitoring && checkRateLimit()) {
    Api.request(ApiServices.ERROR, {
      type: ApiError.CRIT,
      data: data
    }).catch(() => {});
  }
};

/**
 * Accède à une information d'un service.
 *
 * @param service Le service associé à l'info demandée.
 * @param selector Le sélecteur de l'information (ex: 'my.property').
 * @return La propriété correspondant au sélecteur, null si elle n'existe pas.
 */
Api.get = function (service: ApiService, selector: string): ?any {
  // On regarde si l'information est déjà en cache
  let info = getCacheInfo(service, selector);

  if (info !== undefined) {
    return info;
  }

  if (info) {
    // On parse les infos avec le selecteur donné
    const parts = selector && selector.split('.');
    const count = parts ? parts.length : 0;
    let i = null;

    for (i = 0; i < count; ++i) {
      const part = parts && parts[i];

      if (info.hasOwnProperty(part)) {
        // Partie existante
        info = info[part];
      } else {
        // Partie du sélecteur non-trouvé
        info = null;
        break;
      }
    }

    // On met l'information en cache
    setCacheInfo(service, selector, info);
  }
  return info;
};

Api.setNickname = function (nick: ?string) {
  nickname = nick;
};

Api.setClientId = function (id: ?string) {
  clientid = id;
};

export default Api;
